import React, { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SupportButton from '../components/SupportButton';
import EmailCaptureModal from '../components/EmailCaptureModal';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { 
  Upload,
  Loader2,
  Mail,
  CheckCircle,
  Download,
  ArrowRight,
  Sparkles,
  FileSpreadsheet,
  User,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailToNamePage = () => {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const handleFileSelect = useCallback((selectedFile) => {
    if (!selectedFile) return;
    
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExt = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    
    if (!validTypes.includes(fileExt)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    
    if (selectedFile.size > 50 * 1024 * 1024) {
      toast.error('File size must be under 50MB');
      return;
    }
    
    setFile(selectedFile);
    setResult(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const processFile = async () => {
    if (!file) return;
    
    setProcessing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/tools/email-to-name/process`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        }
      );
      
      setResult(response.data);
      
      if (response.data.total_rows > 100) {
        toast.info(`Extracted names from first 100 rows. ${response.data.total_rows - 100} rows remaining - sign up to process the rest.`);
      } else {
        toast.success('Name extraction completed!');
      }
      
    } catch (error) {
      console.error('Processing error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to process file';
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const resetTool = () => {
    setFile(null);
    setResult(null);
  };

  const handleDownload = () => {
    if (user) {
      window.location.href = `${API_URL}${result.download_url}`;
    } else {
      setShowEmailModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8 sm:pb-12">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-medium mb-4">
            <Mail className="w-4 h-4" />
            Free Email Name Extractor
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
            Email to First & Last Name
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Extract first and last names from email addresses. Only shows results when we're confident in the detection.
          </p>
        </div>

        {/* Features */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <Mail className="w-6 h-6 text-indigo-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">Smart Parsing</p>
            <p className="text-sm text-slate-500">john.doe → John Doe</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">Confidence Filter</p>
            <p className="text-sm text-slate-500">Only high-quality results</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <User className="w-6 h-6 text-purple-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">First 100 Free</p>
            <p className="text-sm text-slate-500">No signup required</p>
          </div>
        </div>

        {!result ? (
          /* Upload Section */
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
                ${file ? 'border-indigo-400 bg-indigo-50' : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'}`}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              data-testid="email-to-name-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="hidden"
                data-testid="email-to-name-file-input"
              />
              
              {file ? (
                <div className="space-y-3">
                  <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto">
                    <FileSpreadsheet className="w-7 h-7 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{file.name}</p>
                    <p className="text-sm text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); resetTool(); }}
                  >
                    Change file
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto">
                    <Upload className="w-7 h-7 text-slate-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Drop your CRM export here</p>
                    <p className="text-sm text-slate-500">CSV or Excel with email addresses (max 50MB)</p>
                  </div>
                </div>
              )}
            </div>

            {file && (
              <Button
                onClick={processFile}
                disabled={processing}
                className="w-full mt-6 h-14 text-lg bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="email-to-name-process-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Extracting names...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Extract Names
                  </>
                )}
              </Button>
            )}

            {/* Examples */}
            <div className="mt-6 p-4 bg-slate-50 rounded-xl">
              <p className="text-sm font-medium text-slate-700 mb-3">Supported formats:</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="p-2 bg-white rounded border border-slate-200">
                  <code className="text-indigo-600">john.smith@...</code>
                  <span className="text-slate-400 ml-2">→</span>
                  <span className="text-emerald-600 ml-2">John Smith</span>
                </div>
                <div className="p-2 bg-white rounded border border-slate-200">
                  <code className="text-indigo-600">johnsmith@...</code>
                  <span className="text-slate-400 ml-2">→</span>
                  <span className="text-emerald-600 ml-2">John Smith</span>
                </div>
                <div className="p-2 bg-white rounded border border-slate-200">
                  <code className="text-indigo-600">jsmith@...</code>
                  <span className="text-slate-400 ml-2">→</span>
                  <span className="text-amber-600 ml-2">J Smith</span>
                </div>
                <div className="p-2 bg-white rounded border border-slate-200">
                  <code className="text-indigo-600">smith_john@...</code>
                  <span className="text-slate-400 ml-2">→</span>
                  <span className="text-emerald-600 ml-2">John Smith</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Results Section */
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
                <p className="text-2xl font-bold text-slate-900">{result.total_rows}</p>
                <p className="text-sm text-slate-500">Total Rows</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
                <p className="text-2xl font-bold text-emerald-600">{result.extracted_count}</p>
                <p className="text-sm text-emerald-700">Names Found</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
                <p className="text-2xl font-bold text-amber-600">{result.low_confidence_count}</p>
                <p className="text-sm text-amber-700">Low Confidence</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-center">
                <p className="text-2xl font-bold text-slate-600">{result.extraction_rate}%</p>
                <p className="text-sm text-slate-500">Success Rate</p>
              </div>
            </div>

            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-800 font-medium">Quality over quantity</p>
                  <p className="text-sm text-blue-700 mt-1">
                    We only show names when we're confident in the extraction. 
                    Low-confidence results are hidden to prevent errors in your CRM.
                  </p>
                </div>
              </div>
            </div>

            {/* Preview Table */}
            {result.preview && result.preview.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-200 bg-slate-50">
                  <h3 className="font-semibold text-slate-900">Preview (First 5 rows)</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Row</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">First Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Last Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.preview.map((row, idx) => (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="px-4 py-3 text-sm text-slate-500">{row.row}</td>
                          <td className="px-4 py-3 text-sm font-mono text-slate-700">{row.email}</td>
                          <td className="px-4 py-3">
                            <span className={`text-sm ${row.shown ? 'font-medium text-slate-900' : 'text-slate-400 italic'}`}>
                              {row.first_name || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm ${row.shown ? 'font-medium text-slate-900' : 'text-slate-400 italic'}`}>
                              {row.last_name || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                              row.confidence === 'High' ? 'bg-emerald-100 text-emerald-700' :
                              row.confidence === 'Medium' ? 'bg-amber-100 text-amber-700' :
                              row.confidence === 'Low' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {row.confidence || '-'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Download Section */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">Download Results</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {result.rows_over_limit > 0 
                      ? `First 100 rows processed. ${result.rows_over_limit} rows remaining.`
                      : `All ${result.total_rows} rows processed.`
                    }
                  </p>
                </div>
                <Button
                  onClick={handleDownload}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="email-to-name-download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV
                </Button>
              </div>
              
              {result.rows_over_limit > 0 && (
                <div className="mt-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
                  <p className="text-sm text-indigo-800 font-medium mb-2">
                    Need to process all {result.total_rows} rows?
                  </p>
                  <Link to="/login">
                    <Button variant="outline" size="sm" className="text-indigo-600 border-indigo-200">
                      Sign Up Free
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Process Another */}
            <div className="text-center">
              <Button variant="outline" onClick={resetTool}>
                Process Another File
              </Button>
            </div>
          </div>
        )}

        {/* Use Cases */}
        <div className="mt-12 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-4 text-center">
            Why Extract Names from Emails?
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="flex gap-3">
              <User className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium text-slate-900">CRM Data Enrichment</p>
                <p className="text-sm text-slate-500">Fill in missing first and last name fields in your CRM automatically.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Mail className="w-5 h-5 text-pink-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium text-slate-900">Email Personalization</p>
                <p className="text-sm text-slate-500">Use extracted names for "Hi John" greetings instead of generic messages.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium text-slate-900">Lead Qualification</p>
                <p className="text-sm text-slate-500">Real names indicate real people - filter out generic emails like info@ or sales@.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium text-slate-900">Save Time</p>
                <p className="text-sm text-slate-500">No more manual research to find names for each contact.</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <SupportButton />

      <EmailCaptureModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        tool="email-to-name"
        fileId={result?.download_url?.split('/').pop()}
        toolName="Email to Name Extractor"
        onSuccess={(downloadUrl) => {
          // Trigger download
          window.location.href = `${API_URL}${downloadUrl}`;
        }}
      />
    </div>
  );
};

export default EmailToNamePage;
